import FileSaver from 'file-saver';

import { EsriGeometryFromJson, EsriSpatialReference } from '@/geo/esri';

import {
    cloneDrawBufferSettings,
    cloneDrawMapLabelSettings,
    cloneDrawStyleSettings,
    normalizeDrawIdentifyBufferMode,
    resolveGraphicBufferSettings,
    resolveGraphicIdentifyBufferMode,
    resolveGraphicMapLabelSettings,
    resolveGraphicStyleSettings
} from './settings';
import type { DrawBufferSettings, DrawIdentifyBufferMode, DrawMapLabelSettings, DrawStyleSettings } from './settings';
import type { DrawGraphicLike } from './types';
import type { InstanceAPI } from '@/api';
import type { BaseGeometry } from '@/geo/api';

export const DRAW_SHAPES_FILE_TYPE = 'ramp4-draw-shapes';
export const DRAW_SHAPES_FILE_VERSION = 1;
export const DRAW_SHAPES_FILE_EXTENSION = 'json';

export interface DrawShapeSettingsExport {
    drawStyle: DrawStyleSettings;
    drawBuffer: DrawBufferSettings;
    drawIdentifyBufferMode: DrawIdentifyBufferMode;
    drawMapLabels: DrawMapLabelSettings;
}

export interface DrawShapeExportRecord {
    id?: string;
    type: string;

    /**
     * this has the format of an ESRI geometry, but lacks the .type property.
     * typically only has the coordinate props (e.g. rings, paths, x, y) and spatialReference
     */
    geometry: unknown;
    settings: DrawShapeSettingsExport;
}

export interface DrawShapesExportFile {
    fileType: typeof DRAW_SHAPES_FILE_TYPE;
    version: typeof DRAW_SHAPES_FILE_VERSION;
    exportedAt: string;
    shapes: DrawShapeExportRecord[];
}

export type DrawShapeImportRecord = DrawShapeExportRecord;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const cloneSerializableValue = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const getRecordSetting = (primary: unknown, fallback: unknown): Record<string, unknown> =>
    isRecord(primary) ? primary : isRecord(fallback) ? fallback : {};

const getGeometryJson = (graphic: DrawGraphicLike): unknown => {
    const geometry = graphic.geometry;
    if (!geometry) return undefined;

    const source = typeof (geometry as any).toJSON === 'function' ? (geometry as any).toJSON() : geometry;
    return cloneSerializableValue(source);
};

const getShapeType = (graphic: DrawGraphicLike): string | undefined =>
    typeof graphic.attributes?.type === 'string' ? graphic.attributes.type : graphic.geometry?.type;

export const getDrawShapeId = (graphic: DrawGraphicLike): string | undefined =>
    typeof graphic.attributes?.id === 'string'
        ? graphic.attributes.id
        : typeof graphic.id === 'string'
          ? graphic.id
          : undefined;

export const createDrawShapeExportRecord = (graphic: DrawGraphicLike): DrawShapeExportRecord | undefined => {
    const geometry = getGeometryJson(graphic);
    const type = getShapeType(graphic);
    if (!geometry || !type) return undefined;
    const id = getDrawShapeId(graphic);

    return {
        ...(id ? { id } : {}),
        type,
        geometry,
        settings: {
            drawStyle: resolveGraphicStyleSettings(graphic.attributes),
            drawBuffer: resolveGraphicBufferSettings(graphic.attributes),
            drawIdentifyBufferMode: resolveGraphicIdentifyBufferMode(graphic.attributes),
            drawMapLabels: resolveGraphicMapLabelSettings(graphic.attributes)
        }
    };
};

export const createDrawShapesExportFile = (graphics: DrawGraphicLike[]): DrawShapesExportFile => ({
    fileType: DRAW_SHAPES_FILE_TYPE,
    version: DRAW_SHAPES_FILE_VERSION,
    exportedAt: new Date().toISOString(),
    shapes: graphics.flatMap(graphic => {
        const record = createDrawShapeExportRecord(graphic);
        return record ? [record] : [];
    })
});

export const createDrawShapesFileName = (prefix = 'ramp-draw-shapes'): string => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `${prefix}-${timestamp}.${DRAW_SHAPES_FILE_EXTENSION}`;
};

export const downloadDrawShapes = (graphics: DrawGraphicLike[], fileName?: string): boolean => {
    const exportFile = createDrawShapesExportFile(graphics);
    if (!exportFile.shapes.length) return false;

    const blob = new Blob([JSON.stringify(exportFile, null, 2)], {
        type: 'application/json;charset=utf-8'
    });
    FileSaver.saveAs(blob, fileName ?? createDrawShapesFileName());
    return true;
};

/**
 * Takes a payload and attempts to extract the shapes
 *
 * @param payload typically an export file format, an individual drawing shape, or an array of drawing shapes
 * @returns an array of shape objects, or undefined if the payload is a bad format
 */
export const getPayloadShapes = (payload: unknown): unknown[] | undefined => {
    if (Array.isArray(payload)) return payload;

    if (!isRecord(payload)) return undefined;

    if (Array.isArray(payload.shapes)) {
        const fileType = payload.fileType ?? payload.format;
        return fileType === DRAW_SHAPES_FILE_TYPE ? payload.shapes : undefined;
    }

    return isRecord(payload.geometry) ? [payload] : undefined;
};

const normalizeImportRecord = (payload: unknown): DrawShapeImportRecord | undefined => {
    if (!isRecord(payload) || !isRecord(payload.geometry)) return undefined;

    const attributes = isRecord(payload.attributes) ? payload.attributes : {};
    const settings = isRecord(payload.settings) ? payload.settings : payload;
    const geometryJson = cloneSerializableValue(payload.geometry);
    const geometry = EsriGeometryFromJson(geometryJson);
    if (!geometry) return undefined;

    const type =
        typeof payload.type === 'string'
            ? payload.type
            : typeof attributes.type === 'string'
              ? attributes.type
              : geometry.type;
    const id = typeof payload.id === 'string' ? payload.id : undefined;

    return {
        id,
        type,
        geometry: typeof (geometry as any).toJSON === 'function' ? (geometry as any).toJSON() : geometryJson,
        settings: {
            drawStyle: cloneDrawStyleSettings(
                getRecordSetting(settings.drawStyle, attributes.drawStyle) as Partial<DrawStyleSettings>
            ),
            drawBuffer: cloneDrawBufferSettings(
                getRecordSetting(settings.drawBuffer, attributes.drawBuffer) as Partial<DrawBufferSettings>
            ),
            drawIdentifyBufferMode: normalizeDrawIdentifyBufferMode(
                (settings.drawIdentifyBufferMode ?? attributes.drawIdentifyBufferMode) as DrawIdentifyBufferMode
            ),
            drawMapLabels: cloneDrawMapLabelSettings(
                getRecordSetting(settings.drawMapLabels, attributes.drawMapLabels) as Partial<DrawMapLabelSettings>
            )
        }
    };
};

/**
 * Attempts to parse various shape payloads into a standarized format.
 *
 * @param payload typically an export file format, an individual drawing shape, or an array of drawing shapes
 * @returns an array of normalized drawing shapes (records), or undefined if the payload could not be processed
 */
export const parseDrawShapesPayload = (payload: unknown): DrawShapeImportRecord[] | undefined => {
    const shapes = getPayloadShapes(payload);
    if (Array.isArray(shapes)) {
        if (shapes.length) {
            const records = shapes.map(normalizeImportRecord);
            if (records.every(Boolean)) {
                return records as DrawShapeImportRecord[];
            } else {
                // stuff in our payload could not be normalized. treat payload as bad
                return undefined;
            }
        } else {
            // empty payload. treat as valid, avoid doing pointless parsing
            return [];
        }
    } else {
        // something failed
        return undefined;
    }
};

export const readDrawShapeFiles = async (files: File[]): Promise<DrawShapeImportRecord[]> => {
    const badFileMsg = 'Invalid draw shape file.';

    if (!files.length) {
        throw new Error(badFileMsg);
    }

    const importedShapes: DrawShapeImportRecord[] = [];

    for (const file of files) {
        let payload: unknown;
        try {
            payload = JSON.parse(await file.text());
        } catch {
            throw new Error(badFileMsg);
        }

        const records = parseDrawShapesPayload(payload);

        if (Array.isArray(records)) {
            if (records.length) {
                importedShapes.push(...records);
            } else {
                // empty, avoid doing pointless parsing
                return [];
            }
        } else {
            // something failed
            throw new Error(badFileMsg);
        }
    }

    return importedShapes;
};

/**
 * Converts a drawing graphic thing to a RAMP geometry
 *
 * @param drawShape drawing graphic internal thing
 * @param rampInstance a ramp instance
 * @returns resolves with Ramp geometry and the Drawing Export shape, or undefined if things went weird
 */
export const drawingToRampGeom = (
    drawShape: DrawGraphicLike,
    rampInstance: InstanceAPI
): { ramp: BaseGeometry; draw: DrawShapeExportRecord } | undefined => {
    // note this "export record create" is needed. using the raw graphic from the store
    // explodes things somehow (missing geometry?? doesn't make sense but thats the erro).
    // Maybe can revisit with deep dive through all the methods later, but this WOMM.
    const drawExportShape = createDrawShapeExportRecord(drawShape)!;
    if (!drawExportShape.geometry) {
        return undefined;
    }

    // convert the drawing type to a neutral esri geometry type.
    let esriGeomType: string;

    switch (drawExportShape.type) {
        case 'point':
        case 'polyline':
        case 'multipoint':
        case 'polygon':
            esriGeomType = drawExportShape.type;
            break;

        case 'rectangle':
        case 'circle':
            esriGeomType = 'polygon';
            break;

        default:
            console.error(`Encountered unhandled drawing type ${drawExportShape.type}`);
            return undefined;
    }

    // make an esri-ish geometry, having enough stuff for our esri-to-ramp converter.
    // the converter needs a real esri SR so that gets enhanced
    const esriLikeGeom: any = drawExportShape.geometry;
    esriLikeGeom.type = esriGeomType;
    esriLikeGeom.spatialReference = new EsriSpatialReference(esriLikeGeom.spatialReference);

    return {
        ramp: rampInstance.geo.geom.geomEsriToRamp(esriLikeGeom, drawShape.id),
        draw: drawExportShape
    };
};
